// Email validation regex pattern
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Strong email validation regex (more comprehensive)
export const STRONG_EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Password validation regex (at least 8 characters, 1 uppercase, 1 lowercase, 1 number)
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;

/**
 * Validates email format using regex
 * @param email - Email string to validate
 * @param useStrongValidation - Whether to use comprehensive validation (default: false)
 * @param existingEmails - Array of existing emails to check for duplicates (optional)
 * @returns Object with isValid boolean and error message
 */
export const validateEmail = (
  email: string, 
  useStrongValidation: boolean = false,
  existingEmails: string[] = []
): { isValid: boolean; error?: string } => {
  // Check if email is empty or just whitespace
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  // Check if email is just whitespace characters
  if (email.trim().length === 0) {
    return { isValid: false, error: 'Email cannot be just spaces' };
  }

  // Trim the email for validation
  const trimmedEmail = email.trim();

  const regex = useStrongValidation ? STRONG_EMAIL_REGEX : EMAIL_REGEX;
  
  if (!regex.test(trimmedEmail)) {
    return { 
      isValid: false, 
      error: useStrongValidation 
        ? 'Please enter a valid email address (e.g., user@domain.com)' 
        : 'Please enter a valid email address' 
    };
  }

  // Check if email already exists (case-insensitive)
  if (existingEmails.length > 0) {
    const emailExists = existingEmails.some(existingEmail => 
      existingEmail.toLowerCase() === trimmedEmail.toLowerCase()
    );
    if (emailExists) {
      return { isValid: false, error: 'This email address is already registered' };
    }
  }

  // Additional checks for strong validation
  if (useStrongValidation) {
    const [localPart, domain] = trimmedEmail.split('@');
    
    // Check local part length
    if (localPart.length > 64) {
      return { isValid: false, error: 'Email local part is too long' };
    }
    
    // Check domain length
    if (domain.length > 253) {
      return { isValid: false, error: 'Email domain is too long' };
    }
    
    // Check for consecutive dots
    if (trimmedEmail.includes('..')) {
      return { isValid: false, error: 'Email cannot contain consecutive dots' };
    }
    
    // Check for dots at start or end of local part
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return { isValid: false, error: 'Email local part cannot start or end with a dot' };
    }
  }

  return { isValid: true };
};

/**
 * Validates password strength using regex
 * @param password - Password string to validate
 * @returns Object with isValid boolean and error message
 */
export const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  // Check if password is empty or just whitespace
  if (!password || password.trim() === '') {
    return { isValid: false, error: 'Password is required' };
  }

  // Check if password is just whitespace characters
  if (password.trim().length === 0) {
    return { isValid: false, error: 'Password cannot be just spaces' };
  }

  // Trim the password for validation
  const trimmedPassword = password.trim();

  if (trimmedPassword.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters long' };
  }

  if (!PASSWORD_REGEX.test(trimmedPassword)) {
    return { 
      isValid: false, 
      error: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' 
    };
  }

  return { isValid: true };
};

/**
 * Validates name format
 * @param name - Name string to validate
 * @returns Object with isValid boolean and error message
 */
export const validateName = (name: string): { isValid: boolean; error?: string } => {
  // Check if name is empty or just whitespace
  if (!name || name.trim() === '') {
    return { isValid: false, error: 'Name is required' };
  }

  // Check if name is just whitespace characters
  if (name.trim().length === 0) {
    return { isValid: false, error: 'Name cannot be just spaces' };
  }

  // Trim the name for validation
  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters long' };
  }

  if (trimmedName.length > 50) {
    return { isValid: false, error: 'Name must be less than 50 characters' };
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(trimmedName)) {
    return { isValid: false, error: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
  }

  return { isValid: true };
};

/**
 * Real-time email validation with debouncing
 * @param email - Email string to validate
 * @param callback - Function to call with validation result
 * @param delay - Debounce delay in milliseconds (default: 500)
 */
export const validateEmailRealtime = (
  email: string, 
  callback: (result: { isValid: boolean; error?: string }) => void,
  delay: number = 500
): (() => void) => {
  let timeoutId: NodeJS.Timeout;

  const debouncedValidation = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      const result = validateEmail(email);
      callback(result);
    }, delay);
  };

  debouncedValidation();

  // Return cleanup function
  return () => clearTimeout(timeoutId);
};

/**
 * Formats email for display (masks part of the email)
 * @param email - Email to format
 * @returns Formatted email string
 */
export const formatEmailForDisplay = (email: string): string => {
  if (!email || !email.includes('@')) return email;
  
  const [localPart, domain] = email.split('@');
  const maskedLocal = localPart.length > 2 
    ? localPart.substring(0, 2) + '*'.repeat(localPart.length - 2)
    : localPart;
  
  return `${maskedLocal}@${domain}`;
};

/**
 * Extracts domain from email
 * @param email - Email string
 * @returns Domain part of the email or null if invalid
 */
export const extractEmailDomain = (email: string): string | null => {
  const validation = validateEmail(email);
  if (!validation.isValid) return null;
  
  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : null;
};

/**
 * Checks if a string contains only whitespace characters
 * @param str - String to check
 * @returns True if string is only whitespace
 */
export const isOnlyWhitespace = (str: string): boolean => {
  return str.trim().length === 0;
};

/**
 * Trims and validates input string
 * @param str - String to validate
 * @param fieldName - Name of the field for error messages
 * @returns Object with trimmed value and validation result
 */
export const validateAndTrimInput = (
  str: string, 
  fieldName: string
): { trimmedValue: string; isValid: boolean; error?: string } => {
  if (!str || str.trim() === '') {
    return { 
      trimmedValue: '', 
      isValid: false, 
      error: `${fieldName} is required` 
    };
  }

  if (isOnlyWhitespace(str)) {
    return { 
      trimmedValue: '', 
      isValid: false, 
      error: `${fieldName} cannot be just spaces` 
    };
  }

  return { 
    trimmedValue: str.trim(), 
    isValid: true 
  };
}; 